<template>
  <div class="text-access-page">
    <div class="card">
      <h2>分享的文字内容</h2>
      
      <div v-if="loading" class="loading">
        <div class="loading-spinner"></div>
        <p>正在加载内容...</p>
      </div>
      
      <div v-else-if="error" class="error-message">
        <div class="error-icon">
          <i>!</i>
        </div>
        <p>{{ error }}</p>
        <button class="btn btn-secondary" @click="goToShare">创建新分享</button>
      </div>
      
      <div v-else-if="textData" class="text-content">
        <div v-if="destroyed" class="notice warning">
          <div class="notice-icon">⚠️</div>
          <div class="notice-content">
            <p>此内容已被阅读，链接已失效</p>
            <p class="notice-sub">该分享已设置为阅后即焚，无法再次访问</p>
          </div>
        </div>
        
        <div v-else-if="fileStatus && fileStatus.remainingReads" class="notice info">
          <div class="notice-icon">ℹ️</div>
          <div class="notice-content">
            <p>此内容还可以被阅读 {{ fileStatus.remainingReads }} 次</p>
            <p class="notice-sub">请妥善保存此链接</p>
          </div>
        </div>
        
        <div class="info-section">
          <div class="info-header">
            <div class="info-item author">
              <label>分享者</label>
              <span>{{ textData.authorName || '匿名用户' }}</span>
            </div>
            
            <div class="info-item timestamp">
              <label>分享时间</label>
              <span>{{ formatDate(textData.timestamp) }}</span>
            </div>
          </div>
          
          <div class="info-item content">
            <label>分享内容</label>
            <div class="text-value">
              <p>{{ textData.text }}</p>
            </div>
          </div>
          
          <div class="actions">
            <button class="btn btn-primary" @click="copyText(textData.text)">
              <span class="btn-icon">📋</span>
              <span>复制文字内容</span>
            </button>
            <button class="btn btn-secondary" @click="goToShare">创建我的分享</button>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="copySuccess" class="copy-success">
      复制成功！
    </div>
  </div>
</template>

<script>
import fileService from '../utils/fileService.js'

export default {
  name: 'TextAccessPage',
  data() {
    return {
      textData: null,
      loading: true,
      error: null,
      fileStatus: null,
      destroyed: false,
      copySuccess: false,
      isMobile: false
    }
  },
  mounted() {
    this.loadTextData()
    this.checkMobile()
    // 监听窗口大小变化，以便响应式调整
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    // 移除事件监听器
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    // 检查是否为移动设备
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    
    // 跳转到分享页面
    goToShare() {
      this.$router.push('/share')
    },
    
    async loadTextData() {
      this.loading = true
      this.error = null
      
      try {
        // 从URL参数中获取文件ID
        const fileId = this.$route.params.id
        if (!fileId) {
          throw new Error('未提供分享ID')
        }
        
        console.log('文件ID:', fileId)
        
        try {
          // 从文件服务读取数据
          const result = await fileService.readFile(fileId)
          
          if (result.error) {
            throw new Error(result.error)
          }
          
          // 设置文件状态
          this.fileStatus = {
            destroyed: result.destroyed,
            remainingReads: result.remainingReads
          }
          
          // 解析内容
          const textData = result.content
          
          // 验证数据结构
          if (!textData.text) {
            throw new Error('分享数据格式不正确，缺少文字内容')
          }
          
          this.textData = textData
          this.destroyed = result.destroyed
          this.loading = false
        } catch (readError) {
          console.error('读取文件服务失败:', readError)
          throw readError
        }
      } catch (error) {
        console.error('读取分享数据失败:', error)
        
        // 提供更详细的错误信息
        let errorMessage = '无法读取分享数据'
        
        // 根据错误类型提供具体信息
        if (error instanceof SyntaxError) {
          errorMessage += ': 数据格式错误 - ' + error.message
        } else {
          errorMessage += ': ' + (error.message || '未知错误')
        }
        
        this.error = errorMessage
        this.loading = false
      }
    },
    
    formatDate(dateString) {
      try {
        const date = new Date(dateString)
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        return '未知时间'
      }
    },
    
    copyText(text) {
      try {
        // 创建一个临时文本区域
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        
        // 尝试使用现代Clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text)
            .then(() => {
              this.showCopySuccess()
            })
            .catch(() => {
              // 如果Clipboard API失败，回退到execCommand
              document.execCommand('copy')
              this.showCopySuccess()
            })
        } else {
          // 回退到传统方法
          document.execCommand('copy')
          this.showCopySuccess()
        }
        
        // 移除临时文本区域
        document.body.removeChild(textArea)
      } catch (err) {
        console.error('复制失败:', err)
        alert('复制失败，请手动复制')
      }
    },
    
    showCopySuccess() {
      this.copySuccess = true
      setTimeout(() => {
        this.copySuccess = false
      }, 2000)
    }
  }
}
</script>

<style scoped>
.text-access-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 2px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

.card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 10px;
  transition: all 0.3s ease;
}

h2 {
  text-align: center;
  margin-bottom: 30px;
  color: #07C160;
  font-size: 24px;
  font-weight: 600;
}

/* 加载状态样式 */
.loading {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(7, 193, 96, 0.2);
  border-radius: 50%;
  border-top-color: #07C160;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 错误信息样式 */
.error-message {
  color: #e74c3c;
  padding: 25px;
  text-align: center;
  background-color: #fef0f0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.error-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #e74c3c;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  font-weight: bold;
  margin-bottom: 15px;
}

/* 通知样式 */
.notice {
  padding: 16px;
  margin-bottom: 25px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
}

.notice-icon {
  font-size: 20px;
  margin-right: 12px;
  margin-top: 2px;
}

.notice-content {
  flex: 1;
}

.notice-content p {
  margin: 0;
  line-height: 1.5;
}

.notice-sub {
  font-size: 12px;
  opacity: 0.8;
  margin-top: 5px !important;
}

.notice.warning {
  background-color: #fff8e1;
  border-left: 4px solid #ffc107;
}

.notice.info {
  background-color: #e8f4fd;
  border-left: 4px solid #2196f3;
}

/* 信息区域样式 */
.info-section {
  margin-top: 20px;
}

.info-header {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 25px;
  background-color: #f9f9f9;
  padding: 15px;
  border-radius: 8px;
}

.info-item {
  margin-bottom: 20px;
}

.info-header .info-item {
  flex: 1;
  min-width: 150px;
  margin-bottom: 0;
}

.info-item label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #555;
  font-size: 14px;
}

.info-item span {
  font-size: 16px;
  color: #333;
}

.text-value {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #eee;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
  font-size: 16px;
  max-height: 400px;
  overflow-y: auto;
}

/* 按钮样式 */
.actions {
  margin-top: 30px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: center;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}

.btn-icon {
  margin-right: 8px;
  font-size: 18px;
}

.btn-primary {
  background-color: #07C160;
  color: white;
  box-shadow: 0 2px 8px rgba(7, 193, 96, 0.3);
}

.btn-primary:hover {
  background-color: #06a050;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(7, 193, 96, 0.4);
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #333;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
  transform: translateY(-2px);
}

/* 复制成功提示 */
.copy-success {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(7, 193, 96, 0.9);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  animation: fadeInOut 2s ease;
  z-index: 1000;
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, 20px); }
  20% { opacity: 1; transform: translate(-50%, 0); }
  80% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -20px); }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .text-access-page {
    padding: 15px;
  }
  
  .card {
    padding: 20px;
    border-radius: 10px;
  }
  
  h2 {
    font-size: 20px;
    margin-bottom: 20px;
  }
  
  .info-header {
    flex-direction: column;
    gap: 10px;
  }
  
  .info-header .info-item {
    width: 100%;
  }
  
  .text-value {
    padding: 15px;
    font-size: 15px;
    max-height: 300px;
  }
  
  .actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .btn {
    width: 100%;
    padding: 14px;
  }
  
  .notice {
    padding: 12px;
  }
}
</style>